import React, { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { Add, Delete, Edit, Logout } from "@mui/icons-material";
import SEO from "../SEO";
import { SalesCar } from "../../types/types";
import { generateSlug } from "../../utils/slugUtils";
import ImageUpload from "./ImageUpload";
import { salesCarsApi } from "../../lib/supabase";
import { ColorBars } from "../elements/ColorBars";

interface SalesAdminDashboardProps {
  adminUser?: { name?: string };
  onLogout?: () => void;
}

const formatPrice = (price: number) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(price);

const SalesAdminDashboard: React.FC<SalesAdminDashboardProps> = ({
  adminUser,
  onLogout,
}) => {
  const [cars, setCars] = useState<SalesCar[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCar, setEditingCar] = useState<SalesCar | null>(null);
  const [form, setForm] = useState({
    make: "",
    model: "",
    year: "",
    price: "",
    mileage: "",
    status: "available" as SalesCar["status"],
    featured: false,
    transmission: "",
    fuel: "",
    engine: "",
    body_style: "",
    exterior_color: "",
    interior_color: "",
    description: "",
    features: "",
    images: [] as string[],
    primary_image_index: 0,
  });

  const inventoryCount = useMemo(
    () => cars.filter((car) => car.status !== "sold").length,
    [cars]
  );

  useEffect(() => {
    const loadCars = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await salesCarsApi.getAll();
        setCars(data || []);
      } catch (err: any) {
        setError(`Failed to load sales inventory: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    loadCars();
  }, []);

  const openDialog = (car?: SalesCar) => {
    if (car) {
      setEditingCar(car);
      setForm({
        make: car.make,
        model: car.model,
        year: car.year.toString(),
        price: car.price.toString(),
        mileage: car.mileage.toString(),
        status: car.status,
        featured: Boolean(car.featured),
        transmission: car.transmission || "",
        fuel: car.fuel || "",
        engine: car.engine || "",
        body_style: car.body_style || "",
        exterior_color: car.exterior_color || "",
        interior_color: car.interior_color || "",
        description: car.description || "",
        features: car.features?.join(", ") || "",
        images: car.images || [],
        primary_image_index: car.primary_image_index || 0,
      });
    } else {
      setEditingCar(null);
      setForm({
        make: "",
        model: "",
        year: "",
        price: "",
        mileage: "",
        status: "available",
        featured: false,
        transmission: "",
        fuel: "",
        engine: "",
        body_style: "",
        exterior_color: "",
        interior_color: "",
        description: "",
        features: "",
        images: [],
        primary_image_index: 0,
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setSuccess(null);

      const features = form.features
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);

      const payload = {
        slug: generateSlug(`${form.year} ${form.make} ${form.model}`),
        make: form.make.trim(),
        model: form.model.trim(),
        year: Number(form.year),
        price: Number(form.price),
        mileage: Number(form.mileage),
        status: form.status,
        featured: form.featured,
        transmission: form.transmission || null,
        fuel: form.fuel || null,
        engine: form.engine || null,
        body_style: form.body_style || null,
        exterior_color: form.exterior_color || null,
        interior_color: form.interior_color || null,
        description: form.description || null,
        features: features.length > 0 ? features : null,
        images: form.images,
        primary_image_index: form.primary_image_index || 0,
      };

      if (editingCar) {
        const updated = await salesCarsApi.update(editingCar.id, payload);
        setCars((prev) =>
          prev.map((car) => (car.id === editingCar.id ? updated : car))
        );
        setSuccess("Listing updated successfully.");
      } else {
        const created = await salesCarsApi.create(payload);
        setCars((prev) => [created, ...prev]);
        setSuccess("Listing added successfully.");
      }

      setDialogOpen(false);
    } catch (err: any) {
      setError(`Failed to save listing: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (carId: string) => {
    if (!window.confirm("Remove this listing from inventory?")) return;
    try {
      setIsLoading(true);
      setError(null);
      await salesCarsApi.delete(carId);
      setCars((prev) => prev.filter((car) => car.id !== carId));
      setSuccess("Listing removed.");
    } catch (err: any) {
      setError(`Failed to delete listing: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Sales Admin | JP Performance Cars"
        description="Manage internal sales inventory listings."
        canonical="https://www.jpperformancecars.co.uk/admin"
      />
      <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            backgroundColor: "black",
            borderBottom: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <Toolbar sx={{ justifyContent: "space-between", py: 1 }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                variant="h6"
                sx={{
                  color: "white",
                  fontWeight: 700,
                  letterSpacing: "1px",
                  fontFamily: "Times New Roman, serif",
                }}
              >
                JP Performance Cars
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255,255,255,0.7)",
                  letterSpacing: "1px",
                }}
              >
                Sales Admin • {adminUser?.name || "Admin"} • {inventoryCount}{" "}
                active listings
              </Typography>
            </Box>
            {onLogout && (
              <Button
                variant="outlined"
                onClick={onLogout}
                startIcon={<Logout />}
                sx={{
                  borderColor: "rgba(255,255,255,0.3)",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    borderColor: "white",
                  },
                }}
              >
                Sign Out
              </Button>
            )}
          </Toolbar>
          <Box sx={{ pb: 2, pl: 3 }}>
            <ColorBars />
          </Box>
        </AppBar>

        <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}
          {success && (
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              onClose={() => setSuccess(null)}
            >
              {success}
            </Alert>
          )}
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Inventory Listings
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Maintain vehicle listings shown on the Sales page.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => openDialog()}
              disabled={isLoading}
              sx={{
                background: "linear-gradient(135deg, #DC143C 0%, #B12A37 100%)",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #B12A37 0%, #8B1538 100%)",
                },
              }}
            >
              {isLoading ? "Loading..." : "Add Listing"}
            </Button>
          </Box>

          <Card
            sx={{ borderRadius: 3, boxShadow: "0 10px 30px rgba(0,0,0,0.08)" }}
          >
            <CardContent sx={{ p: 0 }}>
              {isLoading && cars.length === 0 ? (
                <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
                  <CircularProgress sx={{ color: "#DC143C" }} />
                </Box>
              ) : (
                <TableContainer>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Vehicle</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Price</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Mileage</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cars.map((car) => (
                        <TableRow key={car.id} hover>
                          <TableCell>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 600 }}
                            >
                              {car.year} {car.make} {car.model}
                            </Typography>
                            {car.featured && (
                              <Chip
                                label="Featured"
                                size="small"
                                sx={{ mt: 0.5 }}
                              />
                            )}
                          </TableCell>
                          <TableCell>{formatPrice(car.price)}</TableCell>
                          <TableCell>
                            {car.mileage.toLocaleString()} mi
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={car.status}
                              size="small"
                              color={
                                car.status === "available"
                                  ? "success"
                                  : car.status === "reserved"
                                  ? "warning"
                                  : "default"
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              startIcon={<Edit />}
                              onClick={() => openDialog(car)}
                              sx={{ textTransform: "none" }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              startIcon={<Delete />}
                              onClick={() => handleDelete(car.id)}
                              sx={{ textTransform: "none" }}
                            >
                              Remove
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>

        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          fullWidth
        >
          <DialogTitle>
            {editingCar ? "Edit Listing" : "Add New Listing"}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Make"
                  fullWidth
                  value={form.make}
                  onChange={(event) =>
                    setForm({ ...form, make: event.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Model"
                  fullWidth
                  value={form.model}
                  onChange={(event) =>
                    setForm({ ...form, model: event.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Year"
                  fullWidth
                  type="number"
                  value={form.year}
                  onChange={(event) =>
                    setForm({ ...form, year: event.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Price (£)"
                  fullWidth
                  type="number"
                  value={form.price}
                  onChange={(event) =>
                    setForm({ ...form, price: event.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Mileage"
                  fullWidth
                  type="number"
                  value={form.mileage}
                  onChange={(event) =>
                    setForm({ ...form, mileage: event.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={form.status}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        status: event.target.value as SalesCar["status"],
                      })
                    }
                  >
                    <MenuItem value="available">Available</MenuItem>
                    <MenuItem value="reserved">Reserved</MenuItem>
                    <MenuItem value="sold">Sold</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Featured</InputLabel>
                  <Select
                    label="Featured"
                    value={form.featured ? "yes" : "no"}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        featured: event.target.value === "yes",
                      })
                    }
                  >
                    <MenuItem value="yes">Yes</MenuItem>
                    <MenuItem value="no">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Transmission"
                  fullWidth
                  value={form.transmission}
                  onChange={(event) =>
                    setForm({ ...form, transmission: event.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Fuel"
                  fullWidth
                  value={form.fuel}
                  onChange={(event) =>
                    setForm({ ...form, fuel: event.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Engine"
                  fullWidth
                  value={form.engine}
                  onChange={(event) =>
                    setForm({ ...form, engine: event.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Body Style"
                  fullWidth
                  value={form.body_style}
                  onChange={(event) =>
                    setForm({ ...form, body_style: event.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Exterior Color"
                  fullWidth
                  value={form.exterior_color}
                  onChange={(event) =>
                    setForm({ ...form, exterior_color: event.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Interior Color"
                  fullWidth
                  value={form.interior_color}
                  onChange={(event) =>
                    setForm({ ...form, interior_color: event.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  value={form.description}
                  onChange={(event) =>
                    setForm({ ...form, description: event.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Highlights (comma separated)"
                  fullWidth
                  value={form.features}
                  onChange={(event) =>
                    setForm({ ...form, features: event.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <ImageUpload
                  images={form.images}
                  onImagesChange={(images) => setForm({ ...form, images })}
                  primaryImageIndex={form.primary_image_index}
                  onPrimaryImageChange={(index) =>
                    setForm({ ...form, primary_image_index: index })
                  }
                  maxImages={10}
                  storageBucket="sales-images"
                  storageFolder="sales"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setDialogOpen(false)}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={
                isLoading ||
                !form.make ||
                !form.model ||
                !form.year ||
                !form.price
              }
              sx={{
                background: "linear-gradient(135deg, #DC143C 0%, #B12A37 100%)",
                textTransform: "none",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #B12A37 0%, #8B1538 100%)",
                },
              }}
            >
              {isLoading ? "Saving..." : "Save Listing"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default SalesAdminDashboard;
